import { memo, useMemo } from 'react';
import CountUpModule from 'react-countup';
import { BiGridVertical } from 'react-icons/bi';

// react-countup may export as default or named depending on bundler
const CountUp = CountUpModule.default ?? CountUpModule;

const COUNT_UP_BASE = { duration: 1.2, separator: ',', preserveValue: true, redraw: false };

// Props:
//   label       — string, card label (e.g. "Revenue this month")
//   value       — number, passed to CountUp
//   isCurrency  — bool, format as currency using Intl
//   currency    — ISO 4217 code for currency formatting (default GBP). On the
//                 dashboard this is the company base currency from GetSummary.
//   decimals    — number, decimal places for non-currency (default 0)
//   tone        — 'success' | 'warning' | 'danger' | null
//   displayValue — string, completely overrides the CountUp (used for "—" margin)
export default memo(function KpiCard({
    label,
    value = 0,
    isCurrency = false,
    currency = 'GBP',
    decimals = 0,
    tone = null,
    displayValue = null,
}) {
    const toneClass = tone ? `text-${tone}` : '';

    const formattingFn = useMemo(
        () => isCurrency
            ? (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v)
            : undefined,
        [isCurrency, currency],
    );

    return (
        <div className="surface h-100 position-relative d-flex flex-column">
            {/* Title pinned top-left */}
            <div className="d-flex align-items-start justify-content-between">
                <div className="text-muted text-uppercase" style={{ fontSize: '0.68rem', letterSpacing: '0.06em' }}>
                    {label}
                </div>
                <BiGridVertical
                    className="drag-handle text-muted flex-shrink-0 d-none d-md-block"
                    style={{ opacity: 0.4, fontSize: '1rem', marginTop: '-2px' }}
                />
            </div>
            {/* Value centered in remaining space */}
            <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                <div className={`fw-bold ${toneClass}`} style={{ fontSize: '2rem', lineHeight: 1 }}>
                    {displayValue !== null ? (
                        displayValue
                    ) : (
                        <CountUp
                            {...COUNT_UP_BASE}
                            end={value}
                            decimals={isCurrency ? 2 : decimals}
                            formattingFn={formattingFn}
                        />
                    )}
                </div>
            </div>
        </div>
    );
});
